import type { FilterState } from '@/lib/dd-queue/filters'

/** Reference queue-store PIN_KEY — namespaced for SPA (D&D queue). */
export const DD_PIN_KEY = 'dd:pinnedView'

export interface DdPinnedView {
  filters: FilterState
  search: string
  role: string
  scope: string
  columnOrder: string[]
  columnWidths: Record<string, number>
  hiddenColumns: Record<string, true>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFilterState(value: unknown): value is FilterState {
  if (!isRecord(value)) return false
  return (
    typeof value.path === 'string' &&
    typeof value.customer === 'string' &&
    typeof value.status === 'string' &&
    typeof value.age === 'string' &&
    typeof value.vip === 'string' &&
    typeof value.assignee === 'string'
  )
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function isColumnWidths(value: unknown): value is Record<string, number> {
  if (!isRecord(value)) return false
  return Object.values(value).every((v) => typeof v === 'number' && Number.isFinite(v))
}

function isHiddenColumns(value: unknown): value is Record<string, true> {
  if (!isRecord(value)) return false
  return Object.values(value).every((v) => v === true)
}

function isDdPinnedView(value: unknown): value is DdPinnedView {
  if (!isRecord(value)) return false
  return (
    isFilterState(value.filters) &&
    typeof value.search === 'string' &&
    typeof value.role === 'string' &&
    typeof value.scope === 'string' &&
    isStringArray(value.columnOrder) &&
    isColumnWidths(value.columnWidths) &&
    isHiddenColumns(value.hiddenColumns)
  )
}

export function loadDdPinnedView(): DdPinnedView | null {
  try {
    const raw = localStorage.getItem(DD_PIN_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isDdPinnedView(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function saveDdPinnedView(view: DdPinnedView): void {
  try {
    localStorage.setItem(DD_PIN_KEY, JSON.stringify(view))
  } catch {
    // Quota exceeded, private mode, or other storage failure — ignore.
  }
}

export function clearDdPinnedView(): void {
  localStorage.removeItem(DD_PIN_KEY)
}
